/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Mon Jan 29 2024
 **/

import { useEffect, useRef } from "react";

type MediaElementProps = Partial<{
	id: string;
	autoPlay: boolean;
	muted: boolean;
	playsInline: boolean;
	stream: MediaStream;
}>;

class MediaElement {
	private _rootEl: HTMLDivElement | null = null;

	constructor() {
		this._rootEl = document.querySelector("div[id='media']");
		if (!this._rootEl) {
			this._rootEl = document.createElement("div");
			this._rootEl.setAttribute("id", "media");
			this._rootEl.style.display = "none";
			document.querySelector("options[id='data']")?.appendChild(this._rootEl);
		}
	}

	public dispose() {
		const element = document.querySelector("div[id='media']");
		if (element) {
			element.remove();
		}
	}

	public create(type: "video" | "audio", props?: MediaElementProps) {
		const element = document.createElement(type);
		props?.id && element.setAttribute("id", props.id);
		props?.autoPlay && element.setAttribute("autoplay", `${props.autoPlay}`);
		props?.muted && element.setAttribute("muted", `${props.muted}`);
		props?.playsInline && element.setAttribute("playsinline", `${props.playsInline}`);
		if (props?.stream) {
			element.srcObject = props.stream;
		}
		this._rootEl?.appendChild(element);
		return element;
	}

	public destroy(id: string, type: "video" | "audio") {
		const element = this.find(id, type);
		element && element.remove();
	}

	public find(id: string, type: "video" | "audio"): HTMLMediaElement {
		return document.querySelector(`${type}[id='${id}']`) as HTMLMediaElement;
	}

	public video = {
		find: (id: string) => this.find(id, "video"),
		destroy: (id: string) => this.destroy(id, "video")
	};

	public audio = {
		find: (id: string) => this.find(id, "audio"),
		destroy: (id: string) => this.destroy(id, "audio")
	};
}

export default function useMediaElement() {
	const mediaElementRef = useRef(new MediaElement());

	const effector = {
		onMount() {
			return () => mediaElementRef.current.dispose();
		}
	};

	useEffect(effector.onMount, []);

	return mediaElementRef.current;
}
